<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import type { CustomOptionGroup, CustomOptionValue } from "../api/products";
import CustomParameterRow from "./CustomParameterRow.vue";
import { supportsDimensions, dimensionFields, dimensionConfig } from "./custom-parameters/dimensions";
import { isSharedStickerMaterial } from "../views/sticker-material";
import { activeValues, addNames, cloneGroups, parameterKind, priceCents, quantityNames, removeValue, reorderValues, validateLocalImage,
  type ParameterBridge, type ParameterTemplate, type ParameterKind } from "./custom-parameters/model";

const props = withDefaults(defineProps<{
  modelValue: CustomOptionGroup[];
  publicParameters?: CustomOptionGroup[];
  templates?: ParameterTemplate[];
  disabled?: boolean;
  rowsOnly?: boolean;
  readOnlyCodes?: string[];
  showInactive?: boolean;
  editableGroupNames?: boolean;
  kinds?: Record<string, ParameterKind>;
  fallbackWithoutImage?: boolean;
  persistentImages?: boolean;
  dimensionSettings?: boolean;
  bridge?: ParameterBridge;
}>(), { readOnlyCodes: () => [], publicParameters: () => [], kinds: () => ({}), templates: () => [{ name: "包装盒配置", codes: ["BOX_TYPE", "MATERIAL", "FINISHED_SIZE", "EST_QTY", "QUANTITY", "PRINT", "PAPER_WEIGHT", "LAMINATION", "SURFACE_FINISH", "INNER_TRAY", "ACCESSORIES"] }], disabled: false, bridge: () => ({}) });
const emit = defineEmits<{
  "update:modelValue": [groups: CustomOptionGroup[]];
  "reorder": [groups: CustomOptionGroup[]];
  "price-draft": [prices: { valueId: string; add_price: number }[]];
  "image-reading": [reading: boolean];
  "image-draft": [images: { groupId: string; valueId: string; file: File }[]];
}>();
// Retain each product's original selection for cancel/reselect without mutating the public library.
const snapshots = ref<CustomOptionGroup[]>([]);
watch(() => props.modelValue, () => {
  for (const group of props.modelValue) {
    if (!snapshots.value.some(item => item.code === group.code)) snapshots.value.push(cloneGroups([group])[0]);
  }
}, { immediate: true, flush: "sync" });
const catalog = computed(() => {
  const byCode = new Map(snapshots.value.map(group => [group.code, group]));
  for (const group of props.publicParameters) byCode.set(group.code, group);
  return [...byCode.values()].sort((a, b) => a.sortOrder - b.sortOrder);
});
const localPrices = ref<Record<string, number>>({});
// Local files/previews are a sidecar, NEVER blob/data URLs in persisted imageUrl.
const localImages = ref<Record<string, { file: File; preview: string }>>({});
let imageReadVersion = 0;
const imageRequests = new Map<string, number>();
const readingImages = ref<Record<string, boolean>>({});
watch(readingImages, value => emit("image-reading", Object.values(value).some(Boolean)), { deep: true, flush: "sync" });
const root = ref<HTMLElement | null>(null);
const nameEditor = ref<{ groupId: string; valueId: string; name: string } | null>(null);
const inputs = ref<Record<string, string>>({});
const error = ref("");
const notice = ref("");
const drag = ref<{ groupId: string; valueId?: string } | null>(null);
const editor = ref<{ groupId: string; valueId: string; name: string; price: string } | null>(null);
const editorElement = ref<HTMLElement | null>(null);
const popoverPosition = ref({ left: "16px", top: "16px" });
let returnFocus: HTMLElement | null = null;
const groups = computed(() => props.modelValue.filter(group => props.showInactive || group.isActive).sort((a, b) => a.sortOrder - b.sortOrder));
const hasPrice = computed(() => Boolean(props.bridge.readPrice && props.bridge.writePrice));
const hasCustom = computed(() => Boolean(props.bridge.readCustom && props.bridge.writeCustom));
function kindOf(group: CustomOptionGroup) {
  const automatic = parameterKind(group), override = props.kinds?.[group.id];
  if (automatic === "number-input" && override !== "quantity") return automatic;
  if (automatic === "text-input" && override !== "size") return automatic;
  return override ?? automatic;
}
function imageOf(value: CustomOptionValue) { return localImages.value[value.id]?.preview || value.imageUrl?.trim() || null; }
function emitImages(draft = props.modelValue) {
  emit("image-draft", draft.filter(group => group.isActive).flatMap(group => activeValues(group).flatMap(value => {
    const image = localImages.value[value.id];
    return image ? [{ groupId: group.id, valueId: value.id, file: image.file }] : [];
  })));
}
function cancelImageRead(valueId: string) {
  imageRequests.delete(valueId); delete readingImages.value[valueId];
}
function chooseCardImage(group: CustomOptionGroup, value: CustomOptionValue, event: Event) {
  const input = event.target as HTMLInputElement, file = input.files?.[0];
  input.value = "";
  if (!file || locked(group) || kindOf(group) !== "image" || !value.isActive) return;
  cancelImageRead(value.id);
  error.value = "";
  try { validateLocalImage(file); } catch (failure) {
    error.value = failure instanceof Error ? failure.message : "无法读取图片"; return;
  }
  const version = ++imageReadVersion;
  imageRequests.set(value.id, version); readingImages.value[value.id] = true;
  const reader = new FileReader();
  reader.onload = () => {
    if (imageRequests.get(value.id) !== version) return;
    cancelImageRead(value.id);
    const current = props.modelValue.find(item => item.id === group.id);
    if (!current || locked(current) || !current.values.some(item => item.id === value.id && item.isActive)) return;
    localImages.value[value.id] = { file, preview: String(reader.result) };
    emitImages(); notice.value = `${value.name}图片已更新到本地草稿`;
  };
  reader.onerror = () => {
    if (imageRequests.get(value.id) !== version) return;
    cancelImageRead(value.id); error.value = "读取图片失败，请重新选择";
  };
  reader.readAsDataURL(file);
}
async function editCardName(group: CustomOptionGroup, value: CustomOptionValue) {
  if (locked(group)) return;
  error.value = "";
  nameEditor.value = { groupId: group.id, valueId: value.id, name: value.name };
  await nextTick();
  const input = root.value?.querySelector<HTMLInputElement>(".card-name-input");
  input?.focus(); input?.select();
}
function cancelCardName() { nameEditor.value = null; }
function saveCardName() {
  attempt(() => {
    const draft = nameEditor.value;
    if (!draft) return;
    const group = props.modelValue.find(item => item.id === draft.groupId);
    if (!group || locked(group)) { cancelCardName(); return; }
    const name = draft.name.trim();
    if (!name || name.length > 100) throw new Error("名称需为 1–100 个字符");
    if (group.values.some(value => value.id !== draft.valueId && value.isActive && value.name === name)) throw new Error("该名称已存在");
    const next = cloneGroups([group])[0], value = next.values.find(item => item.id === draft.valueId && item.isActive);
    if (!value) { cancelCardName(); return; }
    value.name = name; cancelCardName(); change(next);
  });
}
onBeforeUnmount(() => { imageRequests.clear(); });

function selected(code: string) { return groups.value.some(group => group.code === code); }
function emitPrices(draft = props.modelValue) {
  emit("price-draft", draft.filter(group => group.isActive).flatMap(group => activeValues(group)
    .map(value => ({ valueId: value.id, add_price: priceOf(value) }))));
}
function priceOf(value: CustomOptionValue) {
  return hasPrice.value ? props.bridge.readPrice!(value) : localPrices.value[value.id] ?? 0;
}
function parameterSelection(source: CustomOptionGroup): CustomOptionGroup {
  const own = snapshots.value.find(group => group.code === source.code);
  const restored = cloneGroups([own ?? source])[0];
  if (!own || !["SINGLE_SELECT", "MULTI_SELECT"].includes(own.inputType) || activeValues(own).length || isSharedStickerMaterial(own)) return restored;
  // Explicit selection can fill an empty product group from current public defaults.
  // Keep historical IDs/JSON and inactive options; never replace a non-empty product selection.
  for (const value of activeValues(cloneGroups([source])[0])) {
    const existing = restored.values.find(item => item.code === value.code);
    if (existing) {
      existing.name = value.name;
      existing.imageUrl = value.imageUrl;
      existing.sortOrder = value.sortOrder;
      existing.isActive = true;
    } else {
      restored.values.push({ ...value, id: `draft:${crypto.randomUUID()}`, groupId: restored.id });
    }
  }
  return restored;
}
function toggleParameter(source: CustomOptionGroup) {
  if (props.disabled || isSharedStickerMaterial(source) || (!source.isActive && source.id.startsWith("draft:"))) return;
  const current = props.modelValue.find(group => group.code === source.code);
  if (current && isSharedStickerMaterial(current)) return;
  if (current?.isActive) {
    delete inputs.value[current.id];
    for (const value of current.values) { delete localPrices.value[value.id]; delete localImages.value[value.id]; cancelImageRead(value.id); }
    if (editor.value?.groupId === current.id) closeEditor();
    if (nameEditor.value?.groupId === current.id) cancelCardName();
    drag.value = null;
    // Remove from local draft only. The host retains its saved baseline for persistence.
    const remaining = props.modelValue.filter(group => group.code !== source.code);
    emit("update:modelValue", remaining);
    emitPrices(remaining);
    emitImages(remaining);
    notice.value = `已取消${source.name}，该行未保存内容已清空`;
    return;
  }
  const restored = parameterSelection(source);
  restored.isActive = true;
  restored.sortOrder = Math.max(-1, ...groups.value.map(group => group.sortOrder)) + 1;
  const next = [...props.modelValue.filter(group => group.code !== source.code), restored];
  emit("update:modelValue", next); emitPrices(next);
}
function applyTemplate(template: ParameterTemplate) {
  if (props.disabled) return;
  const next = cloneGroups(props.modelValue);
  for (const source of catalog.value) {
    if (!template.codes.includes(source.code) || selected(source.code) || isSharedStickerMaterial(source) || (!source.isActive && source.id.startsWith("draft:"))) continue;
    const restored = parameterSelection(source);
    restored.isActive = true;
    restored.sortOrder = Math.max(-1, ...next.map(group => group.sortOrder)) + 1;
    const old = next.findIndex(group => group.code === source.code);
    if (old >= 0) next.splice(old, 1, restored); else next.push(restored);
  }
  emit("update:modelValue", next);
  emitPrices(next);
  notice.value = `已按${template.name}批量勾选，原有编辑内容保留`;
}

function locked(group: CustomOptionGroup) {
  // Existing shared libraries have their own write semantics; the host can disable this editor.
  return props.disabled || !group.isActive || isSharedStickerMaterial(group) || props.readOnlyCodes?.includes(group.code);
}
function change(group: CustomOptionGroup) {
  if (props.disabled) return;
  const next = props.modelValue.map(item => item.id === group.id ? group : item);
  emit("update:modelValue", next); emitPrices(next); emitImages(next);
}
function setDimensionMode(group: CustomOptionGroup, event: Event) {
  if (locked(group)) return;
  const mode = (event.target as HTMLSelectElement).value === "plane" ? "plane" : "solid";
  const fields = dimensionFields(group.config);
  try { change({ ...group, config: dimensionConfig(group.config, mode, fields.flatLength || fields.flatWidth) }); }
  catch (failure) { error.value = failure instanceof Error ? failure.message : "尺寸配置无法修改"; }
}
function setExpandedDimensions(group: CustomOptionGroup, event: Event) {
  if (locked(group) || !dimensionFields(group.config).height) return;
  try { change({ ...group, config: dimensionConfig(group.config, "solid", (event.target as HTMLInputElement).checked) }); }
  catch (failure) { error.value = failure instanceof Error ? failure.message : "尺寸配置无法修改"; }
}
function renameGroup(group: CustomOptionGroup, raw: string) {
  if (locked(group)) return;
  attempt(() => {
    const name = raw.normalize("NFKC").trim();
    if (!name || name.length > 100) throw new Error("大类名称需为1–100个字符");
    if (props.modelValue.some(item => item.id !== group.id && item.name.normalize("NFKC").trim() === name)) throw new Error("已有同名大类");
    change({ ...group, name });
  });
}
function attempt(action: () => void) {
  error.value = "";
  try { action(); } catch (failure) { error.value = failure instanceof Error ? failure.message : "操作未完成，请检查输入"; }
}
function add(group: CustomOptionGroup, event?: KeyboardEvent) {
  if (locked(group) || event?.isComposing || event?.keyCode === 229) return;
  const text = inputs.value[group.id]?.trim();
  if (!text) return;
  attempt(() => {
    change(addNames(group, kindOf(group) === "quantity" ? quantityNames(text) : [text]));
    inputs.value[group.id] = "";
    notice.value = "已添加到草稿";
  });
}
function remove(group: CustomOptionGroup, id: string) {
  if (!locked(group)) {
    delete localPrices.value[id]; delete localImages.value[id]; cancelImageRead(id);
    if (nameEditor.value?.valueId === id) cancelCardName();
    change(removeValue(group, id));
    notice.value = "已从草稿移除；原记录保留";
  }
}
function restore(group: CustomOptionGroup, id: string) {
  if (locked(group)) return;
  const next = cloneGroups([group])[0], value = next.values.find(item => item.id === id);
  if (value) { value.isActive = true; value.sortOrder = Math.max(-1, ...activeValues(group).map(item => item.sortOrder)) + 1; change(next); }
}
async function edit(group: CustomOptionGroup, value: CustomOptionValue, event: Event) {
  if (locked(group)) return;
  returnFocus = event.currentTarget as HTMLElement;
  const bounds = returnFocus.getBoundingClientRect();
  popoverPosition.value = { left: `${Math.max(16, Math.min(bounds.left, window.innerWidth - 316))}px`,
    top: `${Math.max(16, Math.min(bounds.bottom + 8, window.innerHeight - 390))}px` };
  error.value = "";
  editor.value = { groupId: group.id, valueId: value.id, name: value.name,
    price: (priceOf(value) / 100).toFixed(2) };
  await nextTick();
  editorElement.value?.querySelector<HTMLInputElement>('input:not([type="file"])')?.focus();
}
function closeEditor() { editor.value = null; returnFocus?.focus(); }
function applyEdit() {
  attempt(() => {
    if (!editor.value) return;
    const draft = editor.value;
    const source = props.modelValue.find(group => group.id === draft.groupId);
    if (!source || locked(source)) return;
    const name = draft.name.trim();
    if (!name || name.length > 100) throw new Error("名称需为 1–100 个字符");
    if (source.values.some(value => value.id !== draft.valueId && value.isActive && value.name === name)) throw new Error("该名称已存在");
    const group = cloneGroups([source])[0];
    const index = group.values.findIndex(value => value.id === draft.valueId);
    if (index < 0) throw new Error("选项已变化，请重新打开编辑");
    const value = { ...group.values[index], name };
    const cents = priceCents(draft.price);
    group.values[index] = hasPrice.value ? props.bridge.writePrice!(value, cents) : value;
    if (!hasPrice.value) localPrices.value[draft.valueId] = cents;
    change(group);
    closeEditor();
  });
}
function setCustom(group: CustomOptionGroup, event: Event) {
  if (locked(group) || !hasCustom.value) return;
  attempt(() => change(props.bridge.writeCustom!(cloneGroups([group])[0], (event.target as HTMLInputElement).checked)));
}
function startDrag(group: CustomOptionGroup, event: DragEvent, valueId?: string) {
  if (locked(group)) { event.preventDefault(); return; }
  drag.value = { groupId: group.id, valueId };
  event.dataTransfer?.setData("text/plain", valueId ?? group.id);
  if (event.dataTransfer) event.dataTransfer.effectAllowed = "move";
}
// Native HTML Drag & Drop onEnd: only local, validated drag sources are accepted.
function onEnd(targetGroup: CustomOptionGroup, targetValueId?: string) {
  const source = drag.value;
  drag.value = null;
  if (!source || locked(targetGroup)) return;
  if (source.valueId && targetValueId && source.groupId === targetGroup.id) {
    const group = reorderValues(targetGroup, source.valueId, targetValueId);
    const next = props.modelValue.map(item => item.id === group.id ? group : item);
    emit("update:modelValue", next); emit("reorder", next);
  } else if (!source.valueId && !targetValueId && source.groupId !== targetGroup.id) {
    const next = cloneGroups(groups.value);
    const from = next.findIndex(group => group.id === source.groupId);
    const to = next.findIndex(group => group.id === targetGroup.id);
    if (from < 0 || to < 0) return;
    if (next.some(group => props.readOnlyCodes?.includes(group.code))) { notice.value = "含共享只读库时暂不支持大类排序，选项排序仍可使用"; return; }
    next.splice(to, 0, next.splice(from, 1)[0]);
    next.forEach((group, index) => { group.sortOrder = index; });
    const all = props.showInactive ? next : [...next, ...cloneGroups(props.modelValue.filter(group => !group.isActive))];
    emit("update:modelValue", all); emit("reorder", all);
  }
  notice.value = "已更新草稿顺序";
}
function move(group: CustomOptionGroup, valueId: string, offset: number) {
  const values = activeValues(group), index = values.findIndex(value => value.id === valueId);
  const target = values[index + offset];
  if (!target || locked(group)) return;
  drag.value = { groupId: group.id, valueId }; onEnd(group, target.id);
}
</script>

<template>
  <section
    ref="root"
    class="parameter-editor"
    :aria-label="rowsOnly ? '公共参数选项' : '定制商品参数配置'"
  >
    <header
      v-if="!rowsOnly"
      class="parameter-heading"
    >
      <div><h2>定制商品参数配置</h2><p>先勾选公共参数，再编辑下方选项</p></div>
      <span>已选 {{ groups.length }} / {{ catalog.length }}</span>
    </header>
    <section
      v-if="!rowsOnly"
      class="parameter-selector"
      aria-label="所有公共参数"
    >
      <div class="selector-toolbar">
        <h3>所有公共参数</h3>
        <div class="parameter-templates">
          <span>快捷勾选</span>
          <button
            v-for="template in templates"
            :key="template.name"
            type="button"
            :disabled="disabled || !catalog.length"
            @click="applyTemplate(template)"
          >
            {{ template.name }}
          </button>
        </div>
      </div>
      <div class="parameter-grid">
        <button
          v-for="parameter in catalog"
          :key="parameter.code"
          class="parameter-choice"
          :class="{ selected: selected(parameter.code) }"
          type="button"
          role="checkbox"
          :aria-checked="selected(parameter.code)"
          :aria-label="parameter.name"
          :disabled="disabled || isSharedStickerMaterial(parameter) || (!parameter.isActive && parameter.id.startsWith('draft:'))"
          @click="toggleParameter(parameter)"
        >
          <span>{{ parameter.name }}{{ !parameter.isActive && parameter.id.startsWith('draft:') ? '（停用）' : '' }}</span>
          <svg
            v-if="selected(parameter.code)"
            class="choice-check"
            viewBox="0 0 16 16"
            width="16"
            height="16"
            aria-hidden="true"
          ><path d="m4 8 3 3 5-6" /></svg>
        </button>
      </div>
      <p
        v-if="!catalog.length"
        class="parameter-empty"
      >
        暂无公共参数，请通过 publicParameters 传入现有公共参数库。
      </p>
    </section>
    <div
      v-if="groups.length"
      class="editor-toolbar"
    >
      <strong>参数选项</strong><span>回车添加 · 双击编辑 · 拖动排序</span>
    </div>
    <p
      v-if="error"
      class="parameter-error"
      role="alert"
    >
      {{ error }}
    </p>
    <p
      v-if="!groups.length"
      class="parameter-empty"
    >
      {{ rowsOnly ? '暂无公共参数，请先新增大类。' : '勾选上方参数后，在这里编辑选项。' }}
    </p>
    <CustomParameterRow
      v-for="group in groups"
      :key="group.id"
      :group="group"
      :disabled="locked(group)"
      :editable-name="editableGroupNames"
      @rename="renameGroup(group, $event)"
      @drag-start="startDrag(group, $event)"
      @drag-end="drag = null"
      @drop-row="onEnd(group)"
    >
      <template #actions>
        <slot
          name="group-actions"
          :group="group"
        />
      </template>
      <div class="parameter-content">
        <div
          v-if="kindOf(group) === 'number-input' || kindOf(group) === 'text-input'"
          class="parameter-input-preview"
        >
          <input
            :type="kindOf(group) === 'number-input' ? 'number' : 'text'"
            :aria-label="group.name + (kindOf(group) === 'number-input' ? '数字输入预览' : '文字输入预览')"
            :placeholder="kindOf(group) === 'number-input' ? '由客户输入数字' : '由客户输入文字'"
            disabled
          >
          <span>{{ kindOf(group) === 'number-input' ? '数字输入' : '文字输入' }}{{ group.unit ? ' · ' + group.unit : '' }}</span>
        </div>
        <div
          v-else-if="kindOf(group) === 'image'"
          class="image-card-grid"
          :aria-label="group.name + '图片选项'"
        >
          <div
            v-for="(value, index) in activeValues(group)"
            :key="value.id"
            class="image-card"
            :class="{ 'image-card--text': fallbackWithoutImage && !imageOf(value) }"
            :draggable="!locked(group) && nameEditor?.valueId !== value.id"
            @dragstart.stop="startDrag(group, $event, value.id)"
            @dragend="drag = null"
            @dragover.prevent.stop
            @drop.prevent.stop="onEnd(group, value.id)"
          >
            <label
              class="card-upload"
              :class="{ 'has-image': imageOf(value), 'is-disabled': locked(group) }"
            >
              <input
                type="file"
                class="sr-only"
                accept="image/jpeg,image/png,image/webp"
                :aria-label="'上传或替换' + value.name + '图片'"
                :disabled="locked(group)"
                @change="chooseCardImage(group, value, $event)"
              >
              <img
                v-if="imageOf(value)"
                :src="imageOf(value)!"
                :alt="value.name"
                draggable="false"
              >
              <span>{{ readingImages[value.id] ? '读取中…' : imageOf(value) ? '替换图片' : fallbackWithoutImage ? '+' : '添加图片' }}</span>
            </label>
            <input
              v-if="nameEditor?.valueId === value.id"
              v-model="nameEditor.name"
              class="card-name-input"
              :aria-label="'编辑' + value.name + '名称'"
              maxlength="100"
              :disabled="locked(group)"
              @keydown.enter.prevent="saveCardName"
              @keydown.esc.stop="cancelCardName"
              @blur="saveCardName"
            >
            <button
              v-else
              type="button"
              class="card-caption"
              :disabled="locked(group)"
              :aria-label="'编辑' + value.name + '名称'"
              title="双击编辑名称"
              @dblclick="editCardName(group, value)"
              @keydown.enter.prevent="editCardName(group, value)"
              @keydown.alt.left.prevent="move(group, value.id, -1)"
              @keydown.alt.right.prevent="move(group, value.id, 1)"
            >
              {{ value.name }}
            </button>
            <button
              class="card-remove"
              type="button"
              :aria-label="'移除' + value.name"
              :disabled="locked(group)"
              @click="remove(group, value.id)"
            >
              <svg
                viewBox="0 0 16 16"
                width="14"
                height="14"
                aria-hidden="true"
              ><path d="m4 4 8 8M12 4l-8 8" /></svg>
            </button>
            <div class="touch-order">
              <button
                type="button"
                :disabled="locked(group) || index === 0"
                :aria-label="value.name + '前移'"
                @click="move(group, value.id, -1)"
              >
                前移
              </button>
              <button
                type="button"
                :disabled="locked(group) || index === activeValues(group).length - 1"
                :aria-label="value.name + '后移'"
                @click="move(group, value.id, 1)"
              >
                后移
              </button>
            </div>
          </div>
          <input
            v-model="inputs[group.id]"
            class="tag-input card-add-input"
            :disabled="locked(group)"
            maxlength="100"
            :aria-label="'添加' + group.name + '选项'"
            placeholder="+ 添加选项"
            @keydown.enter.prevent="add(group, $event)"
          >
        </div>
        <div
          v-else
          class="parameter-values"
        >
          <div
            v-for="(value, index) in activeValues(group)"
            :key="value.id"
            class="parameter-option"
            @dragover.prevent.stop
            @drop.prevent.stop="onEnd(group, value.id)"
          >
            <button
              class="option-body"
              type="button"
              :disabled="locked(group)"
              :draggable="!locked(group)"
              :aria-label="value.name + '，双击或按回车编辑，Alt 加左右键调整顺序'"
              @dragstart.stop="startDrag(group, $event, value.id)"
              @dragend="drag = null"
              @dblclick="edit(group, value, $event)"
              @keydown.enter.prevent="edit(group, value, $event)"
              @keydown.alt.left.prevent="move(group, value.id, -1)"
              @keydown.alt.right.prevent="move(group, value.id, 1)"
            >
              <span>{{ value.name }}</span>
              <small v-if="priceOf(value) > 0">+¥{{ (priceOf(value) / 100).toFixed(2) }}{{ hasPrice ? '' : '（草稿）' }}</small>
            </button>
            <button
              class="remove-option"
              type="button"
              :aria-label="'移除' + value.name"
              :disabled="locked(group)"
              @click="remove(group, value.id)"
            >
              <svg
                viewBox="0 0 16 16"
                width="12"
                height="12"
                aria-hidden="true"
              ><path d="m4 4 8 8M12 4l-8 8" /></svg>
            </button>
            <div class="touch-order">
              <button
                type="button"
                :disabled="locked(group) || index === 0"
                :aria-label="value.name + '前移'"
                @click="move(group, value.id, -1)"
              >
                前移
              </button>
              <button
                type="button"
                :disabled="locked(group) || index === activeValues(group).length - 1"
                :aria-label="value.name + '后移'"
                @click="move(group, value.id, 1)"
              >
                后移
              </button>
            </div>
          </div>
          <input
            v-model="inputs[group.id]"
            class="tag-input"
            :disabled="locked(group)"
            maxlength="600"
            :aria-label="'添加' + group.name + '选项'"
            :placeholder="kindOf(group) === 'quantity' ? '50, 100, 500' : kindOf(group) === 'size' ? '+ 85x54mm' : '+ 添加选项'"
            @keydown.enter.prevent="add(group, $event)"
          >
          <button
            v-if="kindOf(group) === 'quantity'"
            type="button"
            :disabled="locked(group) || !inputs[group.id]?.trim()"
            @click="add(group)"
          >
            生成阶梯
          </button>
        </div>
        <details
          v-if="group.values.some(value => !value.isActive)"
          class="retired-options"
        >
          <summary>停用选项 {{ group.values.filter(value => !value.isActive).length }} 项（已保留）</summary>
          <div
            v-for="value in group.values.filter(value => !value.isActive)"
            :key="value.id"
            class="retired-option"
          >
            <span>{{ value.name }}</span><button
              type="button"
              :disabled="locked(group)"
              @click="restore(group, value.id)"
            >
              恢复
            </button>
          </div>
        </details>
        <div
          v-if="dimensionSettings && supportsDimensions(group)"
          class="dimension-settings"
        >
          <label>
            尺寸类型
            <select
              :aria-label="group.name + '尺寸类型'"
              :value="dimensionFields(group.config).height ? 'solid' : 'plane'"
              :disabled="locked(group)"
              @change="setDimensionMode(group, $event)"
            >
              <option value="plane">平面 · 长 × 宽</option>
              <option value="solid">立体 · 长 × 宽 × 高</option>
            </select>
          </label>
          <label v-if="dimensionFields(group.config).height">
            <input
              type="checkbox"
              :checked="dimensionFields(group.config).flatLength || dimensionFields(group.config).flatWidth"
              :disabled="locked(group)"
              @change="setExpandedDimensions(group, $event)"
            >
            显示展开尺寸
          </label>
          <small>保存定制参数后生效</small>
        </div>
        <label
          v-if="kindOf(group) === 'quantity' || kindOf(group) === 'size'"
          class="parameter-switch"
        >
          <input
            type="checkbox"
            role="switch"
            :checked="bridge.readCustom?.(group) ?? false"
            :disabled="locked(group) || !hasCustom"
            @change="setCustom(group, $event)"
          >
          <span>{{ kindOf(group) === 'quantity' ? '允许客户端自定义输入数量' : '允许客户端自定义尺寸' }}</span>
          <small v-if="!hasCustom">未对接 · 不改变客户端行为</small>
        </label>
      </div>
    </CustomParameterRow>
    <p
      v-if="!rowsOnly"
      class="parameter-footnote"
    >
      取消勾选会清空该行未保存内容。数量/尺寸预设需使用新版体验版；局部加价及自定义开关尚未接入客户端。
      <span v-if="persistentImages">图片在点击“保存定制参数”后上传并保存。</span>
    </p>
    <span
      class="sr-only"
      role="status"
      aria-live="polite"
    >{{ notice }}</span>

    <div
      v-if="editor"
      ref="editorElement"
      class="option-popover"
      :style="popoverPosition"
      role="dialog"
      aria-modal="false"
      aria-label="编辑选项"
      @keydown.esc="closeEditor"
    >
      <form @submit.prevent="applyEdit">
        <label>名称<input
          v-model="editor.name"
          maxlength="100"
          required
        ></label>
        <label>加价（元）<input
          v-model="editor.price"
          inputmode="decimal"
          aria-describedby="price-note"
        ></label>
        <small
          v-if="!hasPrice"
          id="price-note"
        >仅局部草稿 · 未对接计价，不写入旧 JSON</small>
        <p
          v-if="error"
          class="popover-error"
          role="alert"
        >
          {{ error }}
        </p>
        <div class="popover-actions">
          <button
            type="button"
            @click="closeEditor"
          >
            取消
          </button><button
            type="submit"
            class="primary"
            :disabled="disabled"
          >
            完成
          </button>
        </div>
      </form>
    </div>
  </section>
</template>

<style scoped>
.parameter-editor { --ink:#000; --text:#333; --paper:#fff; --wash:#f5f5f5; --line:#e5e5e5; color:var(--text); background:var(--paper); font:14px/1.5 system-ui,"Microsoft YaHei",sans-serif; color-scheme:light; }
.parameter-editor *, .parameter-editor *::before, .parameter-editor *::after { box-sizing:border-box; }
.parameter-editor button,.parameter-editor input { font:inherit; color:inherit; }
.parameter-editor button { cursor:pointer; border:1px solid var(--line); border-radius:4px; background:var(--paper); padding:7px 11px; }
.parameter-editor button:hover:not(:disabled) { border-color:var(--ink); background:var(--wash); }
.parameter-editor input { min-width:0; border:1px solid var(--line); border-radius:4px; padding:8px 10px; background:var(--paper); }
.parameter-editor input::placeholder { color:#666; }
.parameter-editor :focus-visible { outline:2px solid var(--ink); outline-offset:3px; }
.parameter-editor :disabled { cursor:not-allowed; color:#666; background:var(--wash); }
.parameter-heading { display:flex; align-items:start; justify-content:space-between; gap:12px; padding-bottom:12px; }
.parameter-editor h2 { font-size:20px; line-height:1.4; margin:0; color:var(--ink); }
.parameter-heading p { margin:6px 0 0; color:#666; font-size:13px; }
.parameter-heading>span { white-space:nowrap; font-size:12px; color:#666; }
.parameter-selector { padding:0 0 12px; }
.selector-toolbar { display:flex; align-items:center; justify-content:space-between; gap:12px; margin-bottom:8px; flex-wrap:wrap; }
.selector-toolbar h3 { margin:0; font-size:13px; }
.parameter-templates { display:flex; align-items:center; gap:8px; flex-wrap:wrap; font-size:12px; }
.parameter-templates>span { color:#666; }
.parameter-editor .parameter-templates button { padding:4px 10px; }
.parameter-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(100px,1fr)); gap:6px; }
.parameter-editor .parameter-choice { position:relative; text-align:left; min-height:42px; padding:8px 24px 8px 12px; border:1px solid transparent; border-radius:3px; background:var(--wash); }
.parameter-editor .parameter-choice:not(.selected):hover:not(:disabled) { border-color:#999; }
.parameter-editor .parameter-choice.selected { border:1px solid #000; background:#fff; color:#000; font-weight:700; }
.parameter-editor .choice-check { position:absolute; top:5px; right:5px; color:#000; background:transparent; stroke-width:1.5; stroke-linecap:round; stroke-linejoin:round; }
.editor-toolbar { display:flex; align-items:center; justify-content:space-between; gap:8px; padding:6px 0; font-size:12px; border-top:1px solid var(--line); }
.editor-toolbar>span { color:#666; }
.parameter-editor svg { fill:none; stroke:currentColor; stroke-width:1.5; vertical-align:middle; }
.parameter-content { min-width:0; display:flex; flex-wrap:wrap; gap:6px 12px; align-items:center; }
.parameter-values { display:flex; flex-wrap:wrap; gap:6px; align-items:center; max-width:100%; }
.parameter-option { display:flex; position:relative; max-width:100%; align-items:center; border:1px solid var(--line); border-radius:4px; background:var(--wash); }
.parameter-editor .option-body { display:flex; align-items:center; gap:8px; max-width:100%; min-height:30px; border:0; background:transparent; padding:4px 12px; cursor:grab; text-align:left; overflow-wrap:anywhere; }
.option-body span { min-width:0; }
.option-body small { white-space:nowrap; }
.parameter-editor .remove-option { flex-shrink:0; padding:6px 8px; border:0; background:transparent; min-height:30px; }
.parameter-editor .tag-input { width:132px; min-height:32px; padding:4px 8px; border-style:dashed; }
.parameter-editor .primary { color:#fff; background:var(--ink); border-color:var(--ink); font-weight:600; padding:9px 16px; }
.parameter-editor .primary:hover:not(:disabled) { color:#fff; background:var(--text); }
.parameter-editor .primary:disabled { background:var(--wash); color:#666; border-color:var(--line); }
.parameter-switch { display:flex; align-items:center; flex-wrap:wrap; gap:6px; font-size:12px; }
.dimension-settings,.dimension-settings label { display:flex; align-items:center; flex-wrap:wrap; gap:8px; font-size:12px; }
.dimension-settings { margin-top:6px; }.dimension-settings small { color:#666; }
.parameter-editor .dimension-settings select { width:auto; min-height:30px; padding:4px 8px; border:1px solid #e5e5e5; border-radius:4px; color:#333; background:#fff; }
.parameter-switch input { appearance:none; width:30px; height:18px; border-radius:20px; padding:0; position:relative; background:var(--line); border-color:var(--line); }
.parameter-switch input::after { content:""; position:absolute; top:2px; left:2px; width:12px; height:12px; border-radius:50%; background:#fff; border:1px solid #999; }
.parameter-switch input:checked { background:#000; border-color:#000; }
.parameter-switch input:checked::after { left:14px; border-color:#fff; }
.parameter-switch small,.parameter-note { color:#666; font-size:12px; }
.parameter-note { margin:8px 0 0; }
.parameter-footnote { margin:4px 0 0; border-top:1px solid var(--line); padding-top:8px; color:#666; font-size:12px; }
.parameter-empty { margin:8px 0; color:#666; font-size:13px; }
.parameter-error { border:1px solid #000; background:var(--wash); padding:10px; }
.option-popover { position:fixed; z-index:30; width:min(300px,calc(100vw - 32px)); max-height:calc(100dvh - 32px); overflow:auto; padding:16px; background:#fff; border:1px solid #000; border-radius:6px; box-shadow:0 8px 24px #0002; }
.option-popover form,.option-popover label { display:grid; gap:6px; }
.option-popover form { gap:12px; }.option-popover small { color:#666; font-size:12px; }
.popover-actions { display:flex; justify-content:flex-end; gap:8px; }
.popover-error { margin:0; font-size:12px; border:1px solid #333; padding:6px; }
.image-card-grid { display:flex; flex-wrap:wrap; align-items:center; gap:10px; width:100%; }
.image-card { position:relative; display:grid; grid-template-rows:124px 32px; gap:4px; width:116px; flex-shrink:0; cursor:grab; }
.card-upload { position:relative; display:grid; place-items:center; width:100%; height:124px; overflow:hidden; background:#ccc; border-radius:8px; cursor:pointer; }
.card-upload:focus-within { outline:2px solid #000; outline-offset:2px; }
.card-upload.is-disabled { cursor:not-allowed; }
.card-upload img { width:100%; height:100%; object-fit:cover; }
.card-upload span { position:absolute; font-size:12px; color:#333; }
.card-upload.has-image span { background:#ffffffe6; padding:3px 6px; opacity:0; }
.card-upload.has-image:hover span,.card-upload.has-image:focus-within span { opacity:1; }
.parameter-editor .card-caption,.parameter-editor .card-name-input { width:100%; height:32px; min-height:0; border:0; border-radius:5px; background:#f5f5f5; padding:4px 7px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; text-align:center; font-size:13px; cursor:text; }
.parameter-editor .card-name-input { outline:1px solid #000; outline-offset:-1px; background:#fff; }
.parameter-editor .card-remove { position:absolute; top:6px; right:6px; display:grid; place-items:center; width:22px; height:22px; min-height:0; border:0; border-radius:50%; padding:2px; background:#fff; color:#000; z-index:1; }
.parameter-editor .card-add-input { width:132px; height:32px; background:transparent; align-self:center; }
.image-card--text { display:flex; align-items:center; gap:0; width:auto; max-width:100%; height:32px; border:1px solid #e5e5e5; background:#f5f5f5; border-radius:4px; }
.image-card--text .card-upload { order:2; width:26px; height:30px; flex-shrink:0; border-radius:0; background:transparent; }
.parameter-editor .image-card--text .card-caption { order:1; width:auto; max-width:180px; height:30px; padding:4px 10px; }
.parameter-editor .image-card--text .card-remove { order:3; position:static; flex-shrink:0; background:transparent; }
.retired-options { width:100%; margin-top:6px; font-size:12px; color:#666; }
.retired-options summary { cursor:pointer; color:#999; }
.retired-option { display:flex; align-items:center; gap:10px; margin-top:6px; }
.parameter-editor .retired-option button { padding:2px 8px; font-size:12px; }
.touch-order { display:none; }.sr-only { position:absolute; width:1px; height:1px; overflow:hidden; clip-path:inset(50%); }
@media (max-width:640px) { .parameter-heading { padding-bottom:10px; }.parameter-editor h2 { font-size:18px; }.editor-toolbar>span { font-size:11px; }.parameter-grid { grid-template-columns:repeat(3,minmax(0,1fr)); }.parameter-editor .parameter-choice { padding-left:8px; font-size:13px; } }
@media (pointer:coarse) { .touch-order { display:flex; }.parameter-option { flex-wrap:wrap; }.parameter-editor .touch-order button { padding:4px 8px; border:0; font-size:12px; }.image-card-grid { row-gap:36px; padding-bottom:26px; }.image-card .touch-order { position:absolute; top:100%; width:100%; justify-content:center; } }
.parameter-input-preview { display:flex; align-items:center; flex-wrap:wrap; gap:10px; color:#666; font-size:13px; }
.parameter-input-preview input { max-width:220px; padding:7px 10px; border:1px solid #e5e5e5; border-radius:4px; background:#f5f5f5; font:inherit; }
</style>
